import { RocketLaunchOutlined } from '@mui/icons-material'
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined'
import {
  Avatar,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import SaveButton from '~/components/createEdit/saveButton'
import CustomDialog from '~/components/customDialog'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import ActionErrorsDisplay from '~/features/campaigns/components/ofuManageCampaign/actionErrorsDisplay'
import useGoLiveValidationErrorsWithSteps, {
  SCHEDULE_STEPS_LOADING_MESSAGE,
} from '~/features/campaigns/hooks/useGoLiveValidationErrorsWithSteps'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignAgentJunctionStatus,
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ECampaignExecutionStatus,
  ECampaignKeys,
  TCampaign,
  TCampaignAgentJunction,
} from '~/features/campaigns/models'
import {
  getCampaignAssistantDisplayName,
  getJunctionExecutionStatus,
  getMissingRequiredAgentMetadataForJunction,
  hasRequiredAgentMetadataForJunction,
  TGoLiveValidationErrorWithStep,
} from '~/features/campaigns/utils'
import { getCampaignJunctions } from '~/features/campaigns/utils/campaignJunctions'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import { ETestId } from '~/models/tests'
import rem from '~/utils/ui'

type TCampaignGoLiveActionProps = {
  hideButton?: boolean
  onRegisterOpenHandler?: (openHandler: () => void) => void
}

export default function CampaignGoLiveAction({
  hideButton = false,
  onRegisterOpenHandler = undefined,
}: TCampaignGoLiveActionProps) {
  const { selectedAgent } = useGetAgentCentralized()
  const { campaign, isArchived } = useGetSelectedCampaign()
  const theme = useTheme()
  const [isGoLiveDialogOpen, setIsGoLiveDialogOpen] = useState(false)
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign(
    campaign?.id || '',
  )
  const campaignJunctions = useMemo(
    () => getCampaignJunctions(campaign as TCampaign | null),
    [campaign],
  )
  const fallbackCampaignAgentId = useMemo(
    () => String(campaign?.[ECampaignKeys.agentId] || '').trim(),
    [campaign],
  )
  const hasMultipleJunctions = campaignJunctions.length > 1

  const {
    isScheduleStepsReady,
    validationErrorsWithSteps: goLiveErrorsWithSteps,
  } = useGoLiveValidationErrorsWithSteps(campaign, { selectedAgent })
  const hasAnyEligibleDraftAssistant = useMemo(() => {
    if (campaignJunctions.length === 0) {
      return Boolean(fallbackCampaignAgentId)
    }
    return campaignJunctions.some(
      (junction) =>
        getJunctionExecutionStatus(junction) ===
          ECampaignAgentJunctionStatus.notStarted &&
        hasRequiredAgentMetadataForJunction({
          campaign: campaign as TCampaign | null,
          junction,
        }),
    )
  }, [campaign, campaignJunctions, fallbackCampaignAgentId])
  const canGoLive =
    !isArchived &&
    !isUpdating &&
    isScheduleStepsReady &&
    goLiveErrorsWithSteps.length === 0 &&
    hasAnyEligibleDraftAssistant

  const getJunctionMetadataErrors = useCallback(
    (junction: TCampaignAgentJunction): TGoLiveValidationErrorWithStep[] => {
      const missingFields = getMissingRequiredAgentMetadataForJunction({
        campaign: campaign as TCampaign | null,
        junction,
      })
      return missingFields.map((field) => ({
        // Formik locationFields keys are keyed by friendly metadata key.
        // Keep this exact path so builder can expand+focus the right input.
        fieldPath: `${ECampaignBuilderFormKeys.sharedCampaignAgentSettings}.${junction.agentId}.locationFields.${String(field.friendlyName || field.variable || '').trim()}`,
        message: `${getCampaignAssistantDisplayName(junction)}: ${field.friendlyName || field.variable || 'Required field'}`,
        friendlyField: `${getCampaignAssistantDisplayName(junction)}: ${field.friendlyName || field.variable || 'Required field'}`,
        stepId: ECampaignBuilderStepIds.multiAssistantSetup,
      }))
    },
    [campaign],
  )

  const allDraftMetadataErrors = useMemo(
    () =>
      campaignJunctions
        .filter(
          (junction) =>
            getJunctionExecutionStatus(junction) ===
            ECampaignAgentJunctionStatus.notStarted,
        )
        .flatMap((junction) => getJunctionMetadataErrors(junction)),
    [campaignJunctions, getJunctionMetadataErrors],
  )

  const selectedMetadataErrors = useMemo(() => {
    const selectedIds = new Set(selectedAgentIds)
    return campaignJunctions
      .filter((junction) => selectedIds.has(junction.agentId))
      .flatMap((junction) => getJunctionMetadataErrors(junction))
  }, [campaignJunctions, getJunctionMetadataErrors, selectedAgentIds])
  const allGoLiveErrorsWithSteps = useMemo(
    () => [...goLiveErrorsWithSteps, ...allDraftMetadataErrors],
    [allDraftMetadataErrors, goLiveErrorsWithSteps],
  )

  let tooltipContent: React.ReactNode = ''
  if (!canGoLive) {
    if (isArchived) {
      tooltipContent = 'Campaign is archived.'
    } else if (isUpdating) {
      tooltipContent = 'Updating…'
    } else if (!isScheduleStepsReady) {
      tooltipContent = SCHEDULE_STEPS_LOADING_MESSAGE
    } else if (allGoLiveErrorsWithSteps.length > 0) {
      tooltipContent = (
        <ActionErrorsDisplay
          validationErrorsWithSteps={allGoLiveErrorsWithSteps}
          campaignId={campaign?.id}
          linkColor={theme.palette.common.white}
          showLabel
        />
      )
    } else {
      tooltipContent = 'Select at least one assistant before going live.'
    }
  }

  const handleGoLiveCancel = () => {
    setIsGoLiveDialogOpen(false)
    setSelectedAgentIds([])
  }

  const handleOpenGoLiveDialog = useCallback(() => {
    if (!canGoLive) {
      return
    }
    setSelectedAgentIds([])
    setIsGoLiveDialogOpen(true)
  }, [canGoLive])

  useEffect(() => {
    if (!onRegisterOpenHandler) {
      return
    }

    onRegisterOpenHandler(handleOpenGoLiveDialog)
  }, [handleOpenGoLiveDialog, onRegisterOpenHandler])

  const handleToggleJunctionSelection = (
    agentId: string,
    shouldSelect: boolean,
  ) => {
    setSelectedAgentIds((previousSelected) => {
      if (shouldSelect) {
        return previousSelected.includes(agentId)
          ? previousSelected
          : [...previousSelected, agentId]
      }
      return previousSelected.filter((id) => id !== agentId)
    })
  }

  const handleRowToggleJunctionSelection = (
    junction: TCampaignAgentJunction,
    isDraft: boolean,
  ) => {
    if (
      !isDraft ||
      !hasRequiredAgentMetadataForJunction({
        campaign: campaign as TCampaign | null,
        junction,
      })
    ) {
      return
    }

    const isSelected = selectedAgentIds.includes(junction.agentId)
    handleToggleJunctionSelection(junction.agentId, !isSelected)
  }
  const selectableDraftAgentIds = useMemo(
    () =>
      campaignJunctions
        .filter(
          (junction) =>
            getJunctionExecutionStatus(junction) ===
              ECampaignAgentJunctionStatus.notStarted &&
            hasRequiredAgentMetadataForJunction({
              campaign: campaign as TCampaign | null,
              junction,
            }),
        )
        .map((junction) => junction.agentId),
    [campaign, campaignJunctions],
  )
  const areAllSelectableDraftsSelected = useMemo(
    () =>
      selectableDraftAgentIds.length > 0 &&
      selectableDraftAgentIds.every((agentId) =>
        selectedAgentIds.includes(agentId),
      ),
    [selectableDraftAgentIds, selectedAgentIds],
  )
  const selectedSelectableDraftCount = useMemo(
    () =>
      selectableDraftAgentIds.filter((agentId) =>
        selectedAgentIds.includes(agentId),
      ).length,
    [selectableDraftAgentIds, selectedAgentIds],
  )
  const handleToggleSelectAllDrafts = useCallback(() => {
    if (areAllSelectableDraftsSelected) {
      setSelectedAgentIds([])
      return
    }
    setSelectedAgentIds(selectableDraftAgentIds)
  }, [areAllSelectableDraftsSelected, selectableDraftAgentIds])

  const getAgentsToStart = useCallback(() => {
    if (isUpdating) {
      return []
    }
    if (campaignJunctions.length === 0) {
      if (!fallbackCampaignAgentId) {
        return []
      }
      return [
        {
          agentId: fallbackCampaignAgentId,
          status: ECampaignAgentJunctionStatus.started,
        },
      ]
    }

    const selectedIds = new Set(
      hasMultipleJunctions
        ? selectedAgentIds
        : campaignJunctions.map((junction) => junction.agentId),
    )

    return campaignJunctions
      .filter(
        (junction) =>
          selectedIds.has(junction.agentId) &&
          getJunctionExecutionStatus(junction) ===
            ECampaignAgentJunctionStatus.notStarted,
      )
      .map((junction) => ({
        agentId: String(junction.agentId),
        status: ECampaignAgentJunctionStatus.started,
      }))
  }, [
    campaignJunctions,
    fallbackCampaignAgentId,
    hasMultipleJunctions,
    isUpdating,
    selectedAgentIds,
  ])

  const handleUpdateCampaignStatus = (newStatus: ECampaignExecutionStatus) => {
    const agentsToStart = getAgentsToStart()
    const hasAnyLiveJunctionAfterUpdate = campaignJunctions.some(
      (junction) =>
        getJunctionExecutionStatus(junction) ===
        ECampaignAgentJunctionStatus.started,
    )
    const hasAnyDraftSelectedToGoLive = agentsToStart.length > 0

    if (hasMultipleJunctions && selectedAgentIds.length === 0) {
      toast.error('Select at least one assistant before going live.')
      return
    }
    const selectedJunctions = campaignJunctions.filter((junction) =>
      selectedAgentIds.includes(junction.agentId),
    )
    const hasSelectionWithMissingMetadata = selectedJunctions.some(
      (junction) =>
        !hasRequiredAgentMetadataForJunction({
          campaign: campaign as TCampaign | null,
          junction,
        }),
    )
    if (hasSelectionWithMissingMetadata) {
      toast.error('Complete required assistant details before going live.')
      return
    }
    if (
      !hasMultipleJunctions &&
      campaignJunctions.some(
        (junction) =>
          getJunctionExecutionStatus(junction) ===
            ECampaignAgentJunctionStatus.notStarted &&
          !hasRequiredAgentMetadataForJunction({
            campaign: campaign as TCampaign | null,
            junction,
          }),
      )
    ) {
      toast.error('Complete required assistant details before going live.')
      return
    }

    updateCampaign(
      {
        [ECampaignKeys.executionStatus]:
          hasAnyLiveJunctionAfterUpdate || hasAnyDraftSelectedToGoLive
            ? newStatus
            : ECampaignExecutionStatus.notStarted,
        ...(agentsToStart.length > 0 && {
          [ECampaignKeys.agents]: agentsToStart,
        }),
      },
      {
        onSuccess: () => {
          toast.success(
            `Campaign ${newStatus === ECampaignExecutionStatus.inProgress ? 'started' : 'paused'} successfully`,
          )
          setIsGoLiveDialogOpen(false)
          setSelectedAgentIds([])
        },
        onError: () => {
          toast.error(
            `Failed to ${newStatus === ECampaignExecutionStatus.inProgress ? 'start' : 'pause'} campaign. Please try again.`,
          )
        },
      },
    )
  }

  const isJunctionDraft = (junction: TCampaignAgentJunction) =>
    getJunctionExecutionStatus(junction) ===
    ECampaignAgentJunctionStatus.notStarted

  const confirmTooltipContent = useMemo(() => {
    if (!hasMultipleJunctions || !isGoLiveDialogOpen) {
      return ''
    }

    if (selectedAgentIds.length === 0) {
      if (allDraftMetadataErrors.length > 0) {
        return (
          <ActionErrorsDisplay
            validationErrorsWithSteps={allDraftMetadataErrors}
            campaignId={campaign?.id}
            linkColor={theme.palette.common.white}
            showLabel
          />
        )
      }
      return 'Select at least one assistant before going live.'
    }

    if (selectedMetadataErrors.length > 0) {
      return (
        <ActionErrorsDisplay
          validationErrorsWithSteps={selectedMetadataErrors}
          campaignId={campaign?.id}
          linkColor={theme.palette.common.white}
          showLabel
        />
      )
    }

    return ''
  }, [
    allDraftMetadataErrors,
    campaign?.id,
    hasMultipleJunctions,
    isGoLiveDialogOpen,
    selectedAgentIds.length,
    selectedMetadataErrors,
    theme.palette.common.white,
  ])

  return (
    <>
      {!hideButton && (
        <Tooltip
          title={tooltipContent}
          placement="bottom"
          leaveDelay={400}
          disableInteractive={false}
        >
          <Box component="span" sx={{ display: 'inline-block' }}>
            <SaveButton
              data-testid={ETestId.CampaignOfuManageGoLive}
              label="Go live"
              sx={{ height: rem(32) }}
              startIcon={<RocketLaunchOutlined />}
              disabled={!canGoLive}
              onClick={handleOpenGoLiveDialog}
            />
          </Box>
        </Tooltip>
      )}

      <CustomDialog
        isOpen={isGoLiveDialogOpen}
        onClose={handleGoLiveCancel}
        onCancel={handleGoLiveCancel}
        title={
          hasMultipleJunctions ? 'Select Assistants to Go Live' : 'Go Live!'
        }
        isDisabled={
          isUpdating || (hasMultipleJunctions && selectedAgentIds.length === 0)
        }
        onConfirm={() =>
          handleUpdateCampaignStatus(ECampaignExecutionStatus.inProgress)
        }
        cancelText="Cancel"
        confirmText="Go Live!"
        shouldKeepCancelEnabled
        confirmTooltip={confirmTooltipContent}
        maxWidth={640}
      >
        <Stack gap={2}>
          <Typography
            variant="body2"
            sx={{
              fontSize: rem(14),
              color: theme.palette.text.secondary,
            }}
          >
            After you mark this campaign as live, most campaign setup options
            will no longer be editable.
          </Typography>

          {hasMultipleJunctions && (
            <Stack gap={1}>
              <Stack direction="row">
                <FormControlLabel
                  sx={{ ml: 1, mr: 0 }}
                  control={
                    <Checkbox
                      checked={areAllSelectableDraftsSelected}
                      indeterminate={
                        selectedSelectableDraftCount > 0 &&
                        !areAllSelectableDraftsSelected
                      }
                      disabled={
                        isUpdating || selectableDraftAgentIds.length === 0
                      }
                      onChange={handleToggleSelectAllDrafts}
                    />
                  }
                  label="Select all"
                />
              </Stack>
              {campaignJunctions.map((junction) => {
                const isDraft = isJunctionDraft(junction)
                const hasRequiredMetadata = hasRequiredAgentMetadataForJunction(
                  {
                    campaign: campaign as TCampaign | null,
                    junction,
                  },
                )
                const assistantName = getCampaignAssistantDisplayName(junction)
                let statusChipLabel = 'Missing required details'
                if (!isDraft) {
                  statusChipLabel = 'Already live'
                } else if (hasRequiredMetadata) {
                  statusChipLabel = 'Location fields complete'
                }
                const statusChipColor =
                  !isDraft || hasRequiredMetadata ? 'success' : 'default'
                return (
                  <Stack
                    key={junction.id || junction.agentId}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    onClick={() =>
                      handleRowToggleJunctionSelection(junction, isDraft)
                    }
                    sx={{
                      border: `${rem(1)} solid ${theme.palette.divider}`,
                      borderRadius: rem(8),
                      px: 1,
                      py: 0.5,
                      cursor: isDraft ? 'pointer' : 'default',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Tooltip
                        title={
                          isDraft && !hasRequiredMetadata ? (
                            <ActionErrorsDisplay
                              validationErrorsWithSteps={getJunctionMetadataErrors(
                                junction,
                              )}
                              campaignId={campaign?.id}
                              linkColor={theme.palette.common.white}
                              showLabel
                            />
                          ) : (
                            ''
                          )
                        }
                        placement="bottom"
                        leaveDelay={400}
                        disableInteractive={false}
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <Checkbox
                            checked={selectedAgentIds.includes(
                              junction.agentId,
                            )}
                            disabled={!isDraft || !hasRequiredMetadata}
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                            onChange={(event) =>
                              handleToggleJunctionSelection(
                                junction.agentId,
                                event.target.checked,
                              )
                            }
                          />
                        </Box>
                      </Tooltip>
                      <Avatar
                        variant="circular"
                        sx={{
                          width: rem(32),
                          height: rem(32),
                          bgcolor: theme.palette.action.selected,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <MapsHomeWorkOutlinedIcon sx={{ fontSize: rem(18) }} />
                      </Avatar>
                      <Typography
                        variant="body1"
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {assistantName}
                      </Typography>
                    </Stack>
                    <Stack sx={{ flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={statusChipLabel}
                        color={statusChipColor}
                        sx={{ width: 'fit-content', borderRadius: rem(6) }}
                      />
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Stack>
      </CustomDialog>
    </>
  )
}
